/* Printora V27 cart — progressive enhancement for the real Shopify product form. */
(() => {
  'use strict';
  let toastTimer = 0;

  const endpoint = (url, fallback) => {
    const value = String(url || fallback || '');
    return value.endsWith('.js') ? value : `${value}.js`;
  };

  function updateCartCount(count) {
    document.querySelectorAll('[data-v27-cart-count]').forEach(node => { node.textContent = String(count || 0); });
  }

  function showToast(copy) {
    const toast = document.querySelector('[data-v27-cart-toast]');
    if (!toast) return false;
    const text = toast.querySelector('[data-v27-cart-toast-copy]');
    if (text && copy) text.textContent = copy;
    window.clearTimeout(toastTimer);
    toast.hidden = false;
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => { toast.hidden = true; }, 180);
    }, 4200);
    return true;
  }

  async function cartCount() {
    const response = await fetch(endpoint(window.routes?.cart, '/cart'), { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Could not refresh cart');
    return response.json();
  }

  function dogfatherPrintColourFieldset(form) {
    const product = form?.closest?.('[data-printora-v27]');
    return product?.querySelector?.('[data-dogfather-print-colour]') || document.querySelector('[data-dogfather-print-colour]');
  }

  function dogfatherPrintInputs(form) {
    const fieldset = dogfatherPrintColourFieldset(form);
    if (!fieldset) return [];
    return [...fieldset.querySelectorAll('input[type="radio"]')];
  }

  function selectedDogfatherPrintColour(form) {
    const checked = dogfatherPrintInputs(form).find(input => input.checked);
    return checked?.value || '';
  }

  function ensureDogfatherHiddenProperty(form) {
    const fieldset = dogfatherPrintColourFieldset(form);
    if (!fieldset || !form) return null;
    let hidden = form.querySelector('input[type="hidden"][data-dogfather-print-colour-value]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'properties[Print colour]';
      hidden.dataset.dogfatherPrintColourValue = 'true';
      form.appendChild(hidden);
    }
    const selected = selectedDogfatherPrintColour(form);
    if (selected) hidden.value = selected;
    return hidden;
  }

  function syncDogfatherHiddenProperty(form) {
    const hidden = ensureDogfatherHiddenProperty(form);
    const value = selectedDogfatherPrintColour(form);
    if (hidden) hidden.value = value;
    return value;
  }

  function propertiesFromForm(form) {
    const properties = {};
    const body = new FormData(form);
    for (const [key, value] of body.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (!match || value instanceof File || value === '') continue;
      properties[match[1]] = String(value);
    }
    return properties;
  }

  async function addDogfather(form, printColour) {
    const variantId = Number(form.querySelector('input[name="id"]')?.value || 0);
    const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value || 1));
    if (!variantId) throw new Error('Please choose an available garment, colour and size.');

    const properties = propertiesFromForm(form);
    properties['Print colour'] = printColour;

    const response = await fetch(endpoint(window.routes?.cart_add_url, '/cart/add'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: variantId, quantity, properties }]
      })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.description || payload.message || 'Could not add this item to cart.');
    const lineItem = payload.items?.[0] || payload;
    if (lineItem?.properties?.['Print colour'] !== printColour) {
      throw new Error('Print colour was not saved with this item. Please remove it and try again.');
    }
    return lineItem;
  }

  function buildFormData(form) {
    const body = new FormData(form);
    const fieldset = dogfatherPrintColourFieldset(form);
    if (fieldset) {
      const printColour = syncDogfatherHiddenProperty(form);
      if (!printColour) throw new Error('Choose a print colour before adding this item.');
      body.set('properties[Print colour]', printColour);
    }
    return body;
  }

  async function add(form, button, options = {}) {
    const original = button?.textContent || '';
    if (button) { button.disabled = true; button.textContent = window.variantStrings?.addingToCart || 'Adding…'; }
    try {
      const fieldset = dogfatherPrintColourFieldset(form);
      let lineItem = null;

      if (fieldset) {
        const printColour = syncDogfatherHiddenProperty(form);
        if (!printColour) throw new Error('Choose a print colour before adding this item.');
        lineItem = await addDogfather(form, printColour);
      } else {
        const body = buildFormData(form);
        const response = await fetch(endpoint(window.routes?.cart_add_url, '/cart/add'), {
          method: 'POST',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          body
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || payload.message || 'Could not add this item to cart.');
        lineItem = payload.items?.[0] || payload;
      }

      const cart = await cartCount();
      updateCartCount(cart.item_count);
      showToast(lineItem?.product_title ? `${lineItem.product_title} is in your basket.` : 'Your item is in your basket.');
      if (options.goToCart) window.location.assign(window.routes?.cart || '/cart');
      return lineItem;
    } catch (error) {
      if (!showToast(error?.message || 'There was a problem adding this item.')) {
        window.alert(error?.message || 'There was a problem adding this item.');
      }
      return null;
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = original || window.variantStrings?.addToCart || 'Add to cart';
      }
    }
  }

  function bindDogfatherCapture() {
    if (document.documentElement.dataset.dogfatherDirectAddBound === '1') return;
    document.documentElement.dataset.dogfatherDirectAddBound = '1';

    document.addEventListener('click', event => {
      const button = event.target.closest?.('#product-add-to-cart');
      if (!button) return;
      const product = button.closest?.('[data-printora-v27]');
      if (!product || product.dataset.productHandle !== 'the-dogfather-t-shirt-or-hoodie-dog-dad-gift-for-men') return;

      const engine = product.querySelector('[data-v27-variant-engine]');
      const productId = engine?.dataset.product;
      const form = productId ? document.getElementById(`product-form-${productId}`) : null;
      if (!form || !dogfatherPrintColourFieldset(form)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (button.disabled || button.dataset.dogfatherAdding === '1') return;

      const printColour = selectedDogfatherPrintColour(form);
      if (!printColour) {
        const first = dogfatherPrintInputs(form)[0];
        first?.focus?.();
        if (!showToast('Choose a print colour before adding this item.')) {
          window.alert('Choose a print colour before adding this item.');
        }
        return;
      }

      button.dataset.dogfatherAdding = '1';
      add(form, button, { goToCart: true }).finally(() => {
        delete button.dataset.dogfatherAdding;
      });
    }, true);
  }

  function bind(root = document) {
    root.querySelectorAll?.('[data-printora-v27]').forEach(product => {
      if (product.dataset.v27CartBound === '1') return;
      product.dataset.v27CartBound = '1';
      const engine = product.querySelector('[data-v27-variant-engine]');
      const productId = engine?.dataset.product;
      const form = productId ? document.getElementById(`product-form-${productId}`) : null;
      if (!form) return;

      const fieldset = dogfatherPrintColourFieldset(form);
      if (fieldset) {
        ensureDogfatherHiddenProperty(form);
        dogfatherPrintInputs(form).forEach(input => {
          input.addEventListener('change', () => syncDogfatherHiddenProperty(form));
        });
        form.addEventListener('formdata', event => {
          const printColour = syncDogfatherHiddenProperty(form);
          if (printColour) event.formData.set('properties[Print colour]', printColour);
        });
      }

      form.addEventListener('submit', event => {
        const button = form.querySelector('[name="add"]');
        if (event.submitter && event.submitter !== button) return;
        if (button?.disabled) { event.preventDefault(); return; }
        event.preventDefault();
        add(form, button);
      });
    });
  }

  bindDogfatherCapture();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => bind(), { once: true });
  else bind();
  document.addEventListener('shopify:section:load', event => bind(event.target || document));
})();

/* Dogfather Textly print colour -> real Shopify line-item property. */
(() => {
  'use strict';
  const HANDLE = 'the-dogfather-t-shirt-or-hoodie-dog-dad-gift-for-men';
  const PRODUCT_ID = '15615008178500';

  const clean = value => String(value || '').replace(/\s+/g, ' ').trim();
  const usableTextField = field => {
    if (!field || field.disabled) return false;
    if (field.tagName === 'TEXTAREA') return true;
    if (field.tagName !== 'INPUT') return false;
    const type = String(field.getAttribute('type') || 'text').toLowerCase();
    return ['text', 'search', 'tel', 'url'].includes(type);
  };

  function root() {
    return document.querySelector(`[data-printora-v27][data-product-handle="${HANDLE}"]`);
  }

  function textlyField(productRoot) {
    if (!productRoot) return null;
    const paletteField = productRoot.querySelector('[data-pv27-textly-print-colours]')?._pv27TextlyField;
    if (usableTextField(paletteField)) return paletteField;

    const blocks = [...productRoot.querySelectorAll('.pv27-app-block')];
    const preferred = blocks.filter(block => {
      const text = String(block.textContent || '').toLowerCase();
      return text.includes('print colour') || text.includes('custom text');
    });
    for (const block of [...preferred, ...blocks.filter(block => !preferred.includes(block))]) {
      const field = [...block.querySelectorAll('input,textarea')].find(usableTextField);
      if (field) return field;
    }
    return null;
  }

  function productForm() {
    return document.getElementById(`product-form-${PRODUCT_ID}`);
  }

  function ensurePropertyInput(form) {
    if (!form) return null;
    let hidden = form.querySelector('input[data-pv27-textly-print-colour-property]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'properties[Print colour]';
      hidden.dataset.pv27TextlyPrintColourProperty = 'true';
      form.appendChild(hidden);
    }
    return hidden;
  }

  function sync() {
    const productRoot = root();
    const form = productForm();
    const field = textlyField(productRoot);
    if (!productRoot || !form || !field) return false;

    const hidden = ensurePropertyInput(form);
    const value = clean(field.value);
    if (hidden) hidden.value = value;

    if (field.dataset.pv27TextlyPropertyBound !== '1') {
      field.dataset.pv27TextlyPropertyBound = '1';
      field.addEventListener('input', sync);
      field.addEventListener('change', sync);
      field.addEventListener('focusout', sync);
    }

    if (form.dataset.pv27TextlyFormdataBound !== '1') {
      form.dataset.pv27TextlyFormdataBound = '1';
      form.addEventListener('formdata', event => {
        const latest = textlyField(root());
        const latestValue = clean(latest?.value);
        const property = ensurePropertyInput(form);
        if (property) property.value = latestValue;
        if (latestValue) event.formData.set('properties[Print colour]', latestValue);
        else event.formData.delete('properties[Print colour]');
      });
    }
    return true;
  }

  function boot() {
    const productRoot = root();
    if (!productRoot) return;
    sync();
    if (productRoot.dataset.pv27TextlyCartPropertyObserver === '1') return;
    productRoot.dataset.pv27TextlyCartPropertyObserver = '1';
    const observer = new MutationObserver(() => sync());
    observer.observe(productRoot, { childList: true, subtree: true });
    window.setTimeout(sync, 250);
    window.setTimeout(sync, 800);
    window.setTimeout(sync, 1800);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', () => window.setTimeout(boot, 0));
})();
